import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import {
  adminActions,
  adminOwnershipDetails,
  getOwnershipDetails,
  isLoadingSelector,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { setDialog } from '../../../../reducers/dialogReducer';
import useTableSort from '../../../../hooks/useTableSort';
import { IDynamicTableObject } from '../../../DynamicTable/types';
import EmptyIncompatibleMatches from '../../IncompatibleMatches/EmptyIncompatibleMatches';
import DynamicTable from '../../../DynamicTable';
import strings from '../../../../localization';

import AllowancesBreakdown, { IAllowancesBreakdown } from './AllowancesBreakdown';
import OwnershipBookingsHeader from './OwnershipBookingsHeader';
import { ownershipBookingsHeader, ownershipBookingsRow } from './helper';

const loadingActions = [adminActions.getOwnershipDetails];

export interface IOwnershipBookingsState {
  ownerId: string;
  startDate: string | null;
  endDate: string | null;
}

const OwnershipBookings = () => {
  const dispatch = useAppDispatch();
  const { state } = useLocation();

  const { ownerId, startDate, endDate } = (state ?? {}) as IOwnershipBookingsState;

  const [page, setPage] = useState(1);
  const { isDesc, orderBy, order, handleSorting } = useTableSort();

  const details = useAppSelector(adminOwnershipDetails);
  const isLoading = useAppSelector((store) =>
    isLoadingSelector(loadingActions, store));

  const { bookings, totalCount, totalPaid, totalCharged } = details ?? {};

  const showAllowances = (allowances: IAllowancesBreakdown) =>
    () => {
      dispatch(
        setDialog({
          heading: { title: strings.allowancesBreakdown },
          showCloseButton: true,
          // eslint-disable-next-line react/jsx-props-no-spreading
          Component: <AllowancesBreakdown {...allowances} />,
        }),
      );
    };

  const fetchBookings = (targetPage: number, sortBy?: string, desc?: boolean) => {
    if (!ownerId) {
      return;
    }

    dispatch(
      getOwnershipDetails({
        ownerId,
        startDate,
        endDate,
        page: targetPage,
        sortBy,
        desc,
      }),
    );
  };

  const table: IDynamicTableObject = {
    headers: ownershipBookingsHeader(),
    rows: ownershipBookingsRow({ bookings, showAllowances }),
    headerBar: [],
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const targetPage = selectedPage + 1;
    setPage(targetPage);
    fetchBookings(targetPage, orderBy, isDesc);
  };

  const emptyContainerComponent = bookings.length === 0
    ? <EmptyIncompatibleMatches message={strings.noDataAvailable} />
    : null;

  useEffect(() => {
    setPage(1);
    fetchBookings(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, startDate, endDate]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      fetchBookings(1, orderBy, isDesc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return (
    <DynamicTable
      table={table}
      showHeader={false}
      loadSuccess={!isLoading}
      emptyContainerComponent={emptyContainerComponent}
      page={page}
      totalCount={totalCount}
      order={order}
      orderBy={orderBy}
      handleSort={handleSorting}
      handlePagination={handlePagination}
      customHeader={(
        <OwnershipBookingsHeader
          totalPaid={totalPaid}
          totalCharged={totalCharged}
        />
      )}
    />
  );
};

export default OwnershipBookings;
